import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X } from "lucide-react";
import { apiGet } from "@/lib/api";
import SeatMap from "@/components/SeatMap";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { useCart } from "@/lib/cart";
import { scrollToSection } from "@/lib/scroll";
import { EVENT, TIERS } from "@/lib/data";

const STEPS = [
  { n: "1", label: "Choose your experience" },
  { n: "2", label: "Pick your exact seats" },
  { n: "3", label: "Make it a gift & reserve" },
];

const Tickets = () => {
  const { seats, total, clear } = useCart();
  const [activeZone, setActiveZone] = useState(null);
  const [zoneAvail, setZoneAvail] = useState({});

  useEffect(() => {
    apiGet("/api/seats")
      .then((d) => {
        const c = {};
        d.zones.forEach((z) => {
          const all = z.rows.flatMap((r) => r.groups.flat());
          c[z.id] = {
            avail: all.filter((s) => s.status === "available").length,
            total: all.length,
          };
        });
        setZoneAvail(c);
      })
      .catch(() => {});
  }, []);

  const onTierClick = (id) => {
    setActiveZone(id);
    scrollToSection("seat-map");
  };

  return (
    <section id="seating" className="relative py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeading
          eyebrow="Seats & Tickets"
          title={
            <>
              All 1,838 seats. <em className="text-gold-gradient italic">Yours to choose.</em>
            </>
          }
          sub="The complete house is on the map below — every zone, every row, every seat. Pick the exact place where you'll sit in the light, then add extras or wrap it as a gift at checkout."
        />

        {/* Step indicator */}
        <Reveal delay={0.1} className="mt-12">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-0">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.n}>
                <div className="glass rounded-2xl px-6 py-4 flex items-center gap-4">
                  <span className="font-cinzel text-lg text-gold-gradient">{s.n}</span>
                  <span className="text-sm text-[#C5CEE0] font-light">{s.label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <span className="hidden sm:block w-10 hairline shrink-0" aria-hidden="true" />
                )}
              </React.Fragment>
            ))}
          </div>
        </Reveal>

        {/* Step 1 — zone cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
          {TIERS.map((t, i) => {
            const active = activeZone === t.id;
            return (
              <Reveal key={t.id} delay={i * 0.06} className="h-full">
                <button
                  data-testid={`tier-card-${t.id}`}
                  onClick={() => !t.soldOut && onTierClick(t.id)}
                  className={`card-lift text-left w-full h-full rounded-3xl p-6 border flex flex-col transition-all ${
                    active
                      ? "border-[#F3D068] bg-[#182243] shadow-[0_0_50px_rgba(212,175,55,0.15)]"
                      : t.soldOut
                      ? "border-white/10 bg-[#0D1226]/70 opacity-70 cursor-default"
                      : "border-white/10 bg-[#131B36]/70"
                  }`}
                >
                  <span className="text-[9px] uppercase tracking-[0.2em] text-[#F3D068] border border-[#D4AF37]/30 rounded-full px-3 py-1 inline-block self-start text-center">
                    {t.badge}
                  </span>
                  <h3 className="font-display text-xl mt-4 text-[#FDFBF7] leading-tight">{t.name}</h3>
                  <p className="font-cinzel text-2xl text-gold-gradient mt-2.5">{t.priceLabel}</p>
                  <p className="text-[11px] text-[#8A99B5] mt-1.5 leading-relaxed">{t.priceNote}</p>
                  {zoneAvail[t.id] && !t.soldOut && (
                    <p
                      className={`text-[11px] mt-2 tracking-wide ${zoneAvail[t.id].avail <= 100 ? "text-[#F59E0B] font-semibold" : "text-[#F59E0B]/80"}`}
                      data-testid={`tier-availability-${t.id}`}
                    >
                      {zoneAvail[t.id].avail <= 100
                        ? `Only ${zoneAvail[t.id].avail} seats left!`
                        : `${zoneAvail[t.id].avail} of ${zoneAvail[t.id].total} seats open`}
                    </p>
                  )}
                  <ul className="mt-4 space-y-2 flex-1">
                    {t.perks.map((p) => (
                      <li key={p} className="flex gap-2 text-xs text-[#C5CEE0] font-light leading-relaxed">
                        <Check size={13} className="text-[#F3D068] shrink-0 mt-0.5" /> {p}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4 text-[10px] tracking-[0.18em] uppercase text-[#F3D068]/90">
                    {t.soldOut
                      ? "Fully reserved"
                      : active
                      ? "Highlighted on the map ↓"
                      : "Tap to highlight on map ↓"}
                  </p>
                </button>
              </Reveal>
            );
          })}
        </div>

        {/* Step 2 — seat map */}
        <Reveal delay={0.1}>
          <div id="seat-map" className="mt-14 glass rounded-[32px] p-3 sm:p-10 relative overflow-hidden scroll-mt-24">
            <div
              className="absolute inset-x-0 top-0 h-40 bg-[radial-gradient(ellipse_at_top,rgba(124,116,242,0.14),transparent_65%)]"
              aria-hidden="true"
            />
            <div className="relative flex flex-wrap items-center justify-between gap-3 mb-6">
              <div>
                <p className="eyebrow">Step 2 · The Full House</p>
                <h3 className="font-display text-2xl sm:text-3xl text-[#FDFBF7] mt-2">
                  {EVENT.venue} · all {EVENT.capacity.toLocaleString()} seats
                </h3>
              </div>
              <p className="text-xs text-[#8A99B5] max-w-xs text-right hidden sm:block">
                Gold seats are the closest to the stage. Every seat you tap is held instantly.
              </p>
            </div>
            <div className="relative">
              <SeatMap activeZone={activeZone} />
            </div>
          </div>
        </Reveal>

        {/* Step 3 — sticky selection bar */}
        {seats.length > 0 && (
          <div className="sticky bottom-5 z-40 mt-8">
            <div className="glass rounded-2xl px-5 sm:px-7 py-4 max-w-3xl mx-auto flex flex-wrap items-center justify-between gap-4 shadow-[0_20px_60px_rgba(0,0,0,0.6)] border-[#F3D068]/30">
              <div className="flex items-center gap-4">
                <span className="font-cinzel text-2xl text-[#F3D068] tabular-nums">{seats.length}</span>
                <div>
                  <p className="text-sm text-[#FDFBF7] font-medium">
                    {seats.length === 1 ? "seat selected" : "seats selected"}
                  </p>
                  <p className="text-xs text-[#8A99B5]">
                    {seats.map((s) => `${s.row}${s.n}`).join(" · ")}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 sm:gap-6">
                <div className="text-right">
                  <p className="font-display text-2xl text-[#FDFBF7]">${total}</p>
                </div>
                <button onClick={clear} data-testid="clear-seats-button" className="text-[#8A99B5] hover:text-[#C5CEE0] transition-colors p-2" aria-label="Clear seats">
                  <X size={18} />
                </button>
                <Link
                  to="/checkout"
                  data-testid="continue-to-checkout-button"
                  className="btn-gold rounded-full px-7 py-3 text-sm font-bold"
                >
                  Continue — Gift & Extras
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Tickets;
